import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogActions, Button, Typography, Box } from "@mui/material";

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean; // 삭제 등 위험한 동작이면 true
  autoConfirmSec?: number; // 지정 시 해당 초가 지나면 자동으로 확인 처리
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ message: "" });
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const resolveRef = useRef<(value: boolean) => void>();

  const confirm: ConfirmFn = useCallback((opts) => {
    const normalized = typeof opts === "string" ? { message: opts } : opts;
    setOptions(normalized);
    setRemainingSec(normalized.autoConfirmSec ?? null);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = (result: boolean) => {
    setOpen(false);
    resolveRef.current?.(result);
  };

  useEffect(() => {
    if (!open || remainingSec === null) return;
    if (remainingSec <= 0) {
      handleClose(true);
      return;
    }
    const timer = setTimeout(() => setRemainingSec((prev) => (prev ?? 1) - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, remainingSec]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          },
        }}
        // window.confirm처럼 ESC로 취소 가능, 배경 클릭으로도 취소
      >
        <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          {options.title && (
            <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 0.5 }}>{options.title}</Typography>
          )}
          <Typography sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.5 }}>{options.message}</Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            pt: 1,
            gap: 1,
          }}
        >
          <Button
            onClick={() => handleClose(false)}
            fullWidth
            sx={{
              borderRadius: "8px",
              color: "text.primary",
              bgcolor: "action.hover",
              "&:hover": { bgcolor: "action.selected" },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {options.cancelText ?? "취소"}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            fullWidth
            autoFocus
            variant="contained"
            color={options.destructive ? "error" : "primary"}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {options.confirmText ?? "확인"}
            {remainingSec !== null && ` (${remainingSec})`}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
};
